import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import Papa from "papaparse";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import RoutePlannerTab from "./components/RoutePlannerTab";
import AirportsTab from "./components/AirportsTab";
import WorldMap from "./components/WorldMap";
import FleetTab from "./components/FleetTab";
import CountriesTab from "./components/CountriesTab";
import NewGameSetupModal from "./components/NewGameSetupModal";
import aircraftCatalog from "./data/aircraftCatalog.json";
import { fallbackAirports } from "./fallbackAirports";
import {
  airportMarkerRadius,
  airportTypeLabels,
  defaultEnabledAirportTypes,
  mergeAirports,
  parseAirports
} from "./utils/airportData";

const saveKey = "airline_tycoon_save_v13";
const csvUrl = "/world-airports.csv";
const dayDurationMs = 300000;
const baseTickMs = 50;
const startMoney = 10000000;

const toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 2200,
  timerProgressBar: true,
  background: "#07111f",
  color: "#edf6ff"
});

const initialStats = {
  flights: 0,
  passengers: 0,
  revenue: 0,
  costs: 0,
  profit: 0,
  bestFlightProfit: 0,
  bestFlightLabel: "",
  longestRoute: 0,
  longestRouteLabel: "",
  diversions: 0,
  emergencies: 0,
  serviceSpent: 0,
  expressFlights: 0
};

const initialGame = {
  cash: startMoney,
  day: 1,
  companyName: "",
  startCountry: "",
  unlockedCountries: [],
  setupComplete: false,
  fleet: [],
  routes: [],
  stats: initialStats,
  expressCooldownUntil: 0,
  log: ["Společnost zatím nebyla založena."]
};

const countryNameFormatter = typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
  ? new Intl.DisplayNames(["cs-CZ", "en"], { type: "region" })
  : null;

function money(value) {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0);
}

function number(value) {
  return new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 0 }).format(value || 0);
}

function haversine(a, b) {
  const earth = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return earth * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function toRad(value) {
  return (value * Math.PI) / 180;
}

function toDeg(value) {
  return (value * 180) / Math.PI;
}

function greatCirclePoint(a, b, progress) {
  const lat1 = toRad(a.lat);
  const lon1 = toRad(a.lon);
  const lat2 = toRad(b.lat);
  const lon2 = toRad(b.lon);
  const distance = 2 * Math.asin(Math.sqrt(Math.sin((lat2 - lat1) / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2));
  if (!distance) return [a.lat, a.lon];
  const sinDistance = Math.sin(distance);
  const x = Math.sin((1 - progress) * distance) / sinDistance;
  const y = Math.sin(progress * distance) / sinDistance;
  const vx = x * Math.cos(lat1) * Math.cos(lon1) + y * Math.cos(lat2) * Math.cos(lon2);
  const vy = x * Math.cos(lat1) * Math.sin(lon1) + y * Math.cos(lat2) * Math.sin(lon2);
  const vz = x * Math.sin(lat1) + y * Math.sin(lat2);
  const lat = Math.atan2(vz, Math.sqrt(vx * vx + vy * vy));
  const lon = Math.atan2(vy, vx);
  return [toDeg(lat), ((toDeg(lon) + 540) % 360) - 180];
}

function greatCirclePositions(a, b, steps) {
  const positions = [];
  for (let i = 0; i <= steps; i++) {
    positions.push(greatCirclePoint(a, b, i / steps));
  }
  return positions;
}

function bearingBetween(from, to) {
  const lat1 = toRad(from[0]);
  const lat2 = toRad(to[0]);
  const dLon = toRad(to[1] - from[1]);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function planeIcon(rotation, ringPercent, ringMode) {
  const percent = Math.max(0, Math.min(100, ringPercent || 0));
  const active = percent > 0 && percent < 100;
  return L.divIcon({
    className: "planeMarker",
    html: `<div class="planeIconShell ${active ? "ringActive" : ""} ${ringMode || ""}" style="--p:${percent * 3.6}deg"><span style="transform: rotate(${rotation}deg)">✈</span></div>`,
    iconSize: [42, 42],
    iconAnchor: [21, 21]
  });
}

function planePoint(origin, destination, state) {
  const from = state.tempFrom || (state.dir === "back" ? destination : origin);
  const to = state.tempTo || (state.dir === "back" ? origin : destination);
  const progress = Math.max(0, Math.min(1, state.progress || 0));
  const position = greatCirclePoint(from, to, progress);
  const next = greatCirclePoint(from, to, Math.min(1, progress + 0.01));
  const rotation = bearingBetween(position, next) + 270;
  return { position, rotation, from, to };
}

function randomLoadFactor() {
  return 0.8 + Math.random() * 0.2;
}

function ticketPrice(distance) {
  if (distance < 450) return 0.34;
  if (distance < 1200) return 0.25;
  if (distance < 2600) return 0.19;
  if (distance < 6500) return 0.155;
  return 0.135;
}

function flightEconomy(route, plane, origin, destination, loadFactor, multiplier) {
  const distance = route.distance;
  const passengers = Math.floor(plane.seats * loadFactor);
  const ticket = Math.max(38, distance * route.ticketPricePerKm + (distance < 500 ? 28 : 42));
  const revenue = passengers * ticket * (multiplier || 1);
  const fuel = distance * plane.fuelPerKm * 0.42;
  const airportSizeFee = origin.type === "large_airport" || destination.type === "large_airport" ? 16 : 9;
  const airportFees = plane.seats * airportSizeFee;
  const staff = Math.max(180, plane.seats * 3.2 + distance * 0.18);
  const maintenance = plane.maintenance / Math.max(2.4, 24 / Math.max(0.7, distance / plane.speed + 0.45));
  const conditionPenalty = Math.max(0, (100 - planeCondition(plane)) * plane.seats * 0.48);
  const costs = fuel + airportFees + staff + maintenance + conditionPenalty;
  const profit = revenue - costs;
  return { loadFactor, passengers, ticket, revenue, costs, profit, fuel, airportFees, staff, maintenance, conditionPenalty };
}

function routeEconomy(route, plane, origin, destination, loadFactorOverride) {
  const oneWayHours = route.distance / plane.speed + 0.45;
  const flightsPerDay = Math.max(0.4, Math.min(12, 20 / Math.max(1.2, oneWayHours)));
  const loadFactor = typeof loadFactorOverride === "number" ? loadFactorOverride : (route.lastLoad || 0.9);
  const oneFlight = flightEconomy(route, plane, origin, destination, loadFactor);
  return {
    ...oneFlight,
    flightsPerDay,
    passengers: oneFlight.passengers * flightsPerDay,
    revenue: oneFlight.revenue * flightsPerDay,
    costs: oneFlight.costs * flightsPerDay,
    profit: oneFlight.profit * flightsPerDay,
    oneFlight
  };
}

function estimateRouteRange(route, plane, origin, destination) {
  const low = routeEconomy(route, plane, origin, destination, 0.8);
  const average = routeEconomy(route, plane, origin, destination, 0.9);
  const high = routeEconomy(route, plane, origin, destination, 1);
  return { low, average, high };
}

function catalogPrice(model) {
  const item = aircraftCatalog.find(function (plane) {
    return plane.model === model;
  });
  return item ? item.price : 0;
}

function planeValue(plane) {
  const base = plane.purchasePrice || catalogPrice(plane.model);
  const agePenalty = Math.min(0.42, (plane.age || 0) * 0.006);
  const condition = typeof plane.condition === "number" ? plane.condition : 100;
  return Math.max(25000, Math.round(base * (0.46 + condition / 420 - agePenalty)));
}

function planeCondition(plane) {
  return Math.max(1, Math.min(100, typeof plane.condition === "number" ? plane.condition : 100));
}

function serviceCost(plane) {
  const missing = 100 - planeCondition(plane);
  return Math.max(0, Math.round((plane.purchasePrice || catalogPrice(plane.model)) * missing * 0.00034));
}

function groundDurationMs(plane) {
  return Math.round(9000 + Math.min(32000, plane.seats * 95));
}

function airportCode(airport) {
  return airport ? (airport.iata || airport.icao || "---") : "---";
}

function makeManifest(route, plane, origin, destination, multiplier) {
  const loadFactor = randomLoadFactor();
  const eco = flightEconomy(route, plane, origin, destination, loadFactor, multiplier || 1);
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    loadFactor,
    passengers: eco.passengers,
    ticket: eco.ticket,
    revenue: eco.revenue,
    costs: eco.costs,
    profit: eco.profit,
    fuel: eco.fuel,
    airportFees: eco.airportFees,
    staff: eco.staff,
    maintenance: eco.maintenance,
    conditionPenalty: eco.conditionPenalty,
    fromId: origin.id,
    toId: destination.id,
    label: `${airportCode(origin)} → ${airportCode(destination)}`,
    createdAt: Date.now()
  };
}

function alertId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function airportTypeLabel(type) {
  return type === "large_airport" ? "velké letiště" : type === "medium_airport" ? "střední letiště" : airportTypeLabels[type] || type;
}

function routeDifficulty(distance, plane, eco) {
  const rangeUse = distance / plane.range;
  const margin = eco.revenue > 0 ? eco.profit / eco.revenue : 0;
  if (rangeUse > 0.92 || margin < 0.08) return { label: "těžká", className: "hard" };
  if (rangeUse > 0.72 || margin < 0.18) return { label: "střední", className: "medium" };
  return { label: "snadná", className: "easy" };
}

function routeAdvice(distance, plane, eco) {
  const rangeUse = distance / plane.range;
  const margin = eco.revenue > 0 ? eco.profit / eco.revenue : 0;
  if (distance > plane.range) return "Letadlo na tuto linku nemá dolet.";
  if (rangeUse > 0.92) return "Dolet je skoro na hraně. Vyplatí se jen při dobré obsazenosti.";
  if (eco.profit < 0) return "Tahle kombinace je ztrátová.";
  if (margin < 0.08) return "Nízká marže. Linka může rychle prodělávat.";
  if (margin > 0.28) return "Silná linka s dobrou rezervou.";
  return "Použitelná linka, ale sleduj náklady.";
}

function getAirport(airports, id) {
  return airports.find(function (airport) {
    return airport.id === id;
  });
}

function getPlane(fleet, id) {
  return fleet.find(function (plane) {
    return plane.id === id;
  });
}

function getCountryName(code) {
  if (!code) return "Neznámá země";
  return countryNameFormatter ? countryNameFormatter.of(code) || code : code;
}

function countryUnlockPrice(country) {
  return Math.round(250000 + country.airportCount * 45000 + country.largeCount * 180000);
}

function buildCountryCatalog(airports) {
  const allowedTypes = new Set(defaultEnabledAirportTypes);
  const map = new Map();
  airports.forEach(function (airport) {
    if (!allowedTypes.has(airport.type)) return;
    if (!airport.country) return;
    const current = map.get(airport.country) || {
      code: airport.country,
      name: getCountryName(airport.country),
      airportCount: 0,
      largeCount: 0
    };
    current.airportCount += 1;
    if (airport.type === "large_airport") current.largeCount += 1;
    map.set(airport.country, current);
  });
  return Array.from(map.values())
    .map(function (country) {
      return { ...country, price: countryUnlockPrice(country) };
    })
    .sort(function (a, b) {
      return a.name.localeCompare(b.name, "cs");
    });
}

function normalizeGame(parsed) {
  const unlockedCountries = Array.isArray(parsed.unlockedCountries) ? parsed.unlockedCountries.filter(Boolean) : [];
  const startCountry = parsed.startCountry || unlockedCountries[0] || "";
  const setupComplete = Boolean(parsed.setupComplete || startCountry || unlockedCountries.length);
  return {
    ...initialGame,
    ...parsed,
    startCountry,
    setupComplete,
    companyName: parsed.companyName || "",
    unlockedCountries: setupComplete ? Array.from(new Set([startCountry, ...unlockedCountries].filter(Boolean))) : [],
    fleet: Array.isArray(parsed.fleet)
      ? parsed.fleet.map(function (plane) {
        return { ...plane, condition: typeof plane.condition === "number" ? plane.condition : 100 };
      })
      : [],
    routes: Array.isArray(parsed.routes) ? parsed.routes : [],
    log: Array.isArray(parsed.log) ? parsed.log : initialGame.log,
    stats: { ...initialStats, ...(parsed.stats || {}) }
  };
}

function loadGame() {
  try {
    const raw = localStorage.getItem(saveKey);
    if (!raw) return initialGame;
    return normalizeGame(JSON.parse(raw));
  } catch {
    return initialGame;
  }
}

export default function App() {
  const [game, setGame] = useState(loadGame);
  const [airports, setAirports] = useState(mergeAirports(fallbackAirports));
  const [loadingAirports, setLoadingAirports] = useState(true);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [selectedPlaneId, setSelectedPlaneId] = useState("");
  const [search, setSearch] = useState("");
  const [speed, setSpeed] = useState(1);
  const [routeProgress, setRouteProgress] = useState({});
  const [activeTab, setActiveTab] = useState("route");
  const [panelOpen, setPanelOpen] = useState(true);
  const [createMode, setCreateMode] = useState(false);
  const [draftRoute, setDraftRoute] = useState({ originId: "", destinationId: "" });
  const [eventAlerts, setEventAlerts] = useState([]);
  const [devOpen, setDevOpen] = useState(false);
  const [devBalance, setDevBalance] = useState(String(initialGame.cash));
  const [devRouteId, setDevRouteId] = useState("");
  const [devEventType, setDevEventType] = useState("diversion");
  const [setupCompanyName, setSetupCompanyName] = useState("");
  const [setupStartCountry, setSetupStartCountry] = useState("");
  const [hasOpenMapPopup, setHasOpenMapPopup] = useState(false);
  const mapCenter = [50.1, 14.26];
  const mapRef = useRef(null);
  const showDevTools = useMemo(function () {
    return new URLSearchParams(window.location.search).has("dev");
  }, []);

  useEffect(function () {
    localStorage.setItem(saveKey, JSON.stringify(game));
  }, [game]);

  useEffect(function () {
    let cancelled = false;
    Papa.parse(csvUrl, {
      download: true,
      header: true,
      complete: function (result) {
        const parsed = parseAirports(result.data || []);
        if (!cancelled && parsed.length > 100) {
          setAirports(parsed);
        }
        if (!cancelled) setLoadingAirports(false);
      },
      error: function () {
        if (!cancelled) {
          setAirports(mergeAirports(fallbackAirports));
          setLoadingAirports(false);
        }
      }
    });
    return function () {
      cancelled = true;
    };
  }, []);

  useEffect(function () {
    const interval = setInterval(function () {
      setGame(function (current) {
        return {
          ...current,
          day: current.day + 1,
          fleet: current.fleet.map(function (plane) {
            return { ...plane, age: (plane.age || 0) + 1 };
          })
        };
      });
    }, dayDurationMs / speed);

    return function () {
      clearInterval(interval);
    };
  }, [speed]);

  useEffect(function () {
    if (devOpen) {
      setDevBalance(String(Math.round(game.cash)));
      if (!devRouteId && game.routes.length) setDevRouteId(game.routes[0].id);
    }
  }, [devOpen, game.cash, game.routes, devRouteId]);

  useEffect(function () {
    if (!game.setupComplete) {
      setSetupCompanyName(game.companyName || "");
      setSetupStartCountry(game.startCountry || "");
    }
  }, [game.companyName, game.setupComplete, game.startCountry]);

  function pushEventAlert(alert) {
    const item = { ...alert, id: alertId(), createdAt: Date.now() };
    setEventAlerts(function (current) {
      return [item, ...current].slice(0, 4);
    });
    setTimeout(function () {
      setEventAlerts(function (current) {
        return current.filter(function (entry) { return entry.id !== item.id; });
      });
    }, 6000);
  }

  function focusFlight(routeId) {
    const route = game.routes.find(function (item) { return item.id === routeId; });
    if (!route) return;
    const origin = getAirport(airports, route.originId);
    const destination = getAirport(airports, route.destinationId);
    const state = routeProgress[route.id] || { progress: 0, dir: "out" };
    if (!origin || !destination || !mapRef.current) return;
    const point = planePoint(origin, destination, state);
    mapRef.current.flyTo(point.position, Math.max(5, mapRef.current.getZoom()), { duration: 0.7 });
  }

  function applyDevBalance() {
    const value = Number(String(devBalance).replace(/[^0-9.-]/g, ""));
    if (!Number.isFinite(value)) {
      toast.fire({ icon: "error", title: "Neplatná částka" });
      return;
    }
    setGame(function (current) {
      return {
        ...current,
        cash: Math.round(value),
        log: [`Dev změna kapitálu: ${money(value)}`, ...current.log].slice(0, 14)
      };
    });
    toast.fire({ icon: "success", title: "Kapitál změněn" });
  }

  const countryCatalog = useMemo(function () {
    return buildCountryCatalog(airports);
  }, [airports]);

  const unlockedCountrySet = useMemo(function () {
    return new Set(game.unlockedCountries || []);
  }, [game.unlockedCountries]);

  const availableAirportsForGame = useMemo(function () {
    if (!game.setupComplete) return [];
    const allowedTypes = new Set(defaultEnabledAirportTypes);
    return airports.filter(function (airport) {
      return allowedTypes.has(airport.type) && unlockedCountrySet.has(airport.country);
    });
  }, [airports, game.setupComplete, unlockedCountrySet]);

  const visibleAirports = useMemo(function () {
    const q = search.trim().toLowerCase();
    return availableAirportsForGame.filter(function (airport) {
      if (!q) return true;
      return `${airport.name} ${airport.iata} ${airport.icao} ${airport.country}`.toLowerCase().includes(q);
    });
  }, [availableAirportsForGame, search]);

  const freePlanes = useMemo(function () {
    const used = new Set(game.routes.map(function (route) { return route.planeId; }));
    return game.fleet.filter(function (plane) { return !used.has(plane.id); });
  }, [game.fleet, game.routes]);

  const draftOrigin = getAirport(airports, draftRoute.originId);
  const draftDestination = getAirport(airports, draftRoute.destinationId);
  const selectedPlane = getPlane(game.fleet, selectedPlaneId);
  const createDistance = draftOrigin && draftDestination ? haversine(draftOrigin, draftDestination) : 0;
  const routePreviewBase = draftOrigin && draftDestination && selectedPlane ? { distance: createDistance, ticketPricePerKm: ticketPrice(createDistance), reputation: 42, lastLoad: 0.9 } : null;
  const routePreview = routePreviewBase ? routeEconomy(routePreviewBase, selectedPlane, draftOrigin, draftDestination, 0.9) : null;
  const routePreviewRange = routePreviewBase ? estimateRouteRange(routePreviewBase, selectedPlane, draftOrigin, draftDestination) : null;
  const routePreviewDifficulty = routePreview && selectedPlane ? routeDifficulty(createDistance, selectedPlane, routePreview) : null;
  const canCreateRoute = draftOrigin && draftDestination && selectedPlane && draftOrigin.id !== draftDestination.id && createDistance <= selectedPlane.range;
  const expressRemaining = Math.max(0, (game.expressCooldownUntil || 0) - Date.now());
  const startCountryName = getCountryName(game.startCountry);

  function nearestAirportFromPoint(point, blockedIds) {
    let best = null;
    let bestDistance = Infinity;
    availableAirportsForGame.forEach(function (airport) {
      if (blockedIds.includes(airport.id)) return;
      if (airport.type !== "large_airport" && airport.type !== "medium_airport") return;
      const distance = haversine({ lat: point[0], lon: point[1] }, airport);
      if (distance < bestDistance) {
        best = airport;
        bestDistance = distance;
      }
    });
    return best;
  }

  function manualEventFor(type, route, plane, origin, destination, position) {
    const nearest = nearestAirportFromPoint(position, [origin.id, destination.id]) || destination;
    if (type === "emergency") {
      return {
        type: "emergency",
        title: "Nouzové přistání",
        description: `${plane.model} musí nouzově přistát na ${airportCode(nearest)}.`,
        targetAirportId: nearest.id,
        resumeToId: destination.id,
        delayMs: 16000 + Math.round(plane.seats * 55),
        penalty: Math.round(plane.seats * 105 + route.distance * 1.9),
        conditionLoss: 7 + Math.random() * 9
      };
    }
    return {
      type: "diversion",
      title: "Odklon letu",
      description: `${plane.model} mění trasu na ${airportCode(nearest)}.`,
      targetAirportId: nearest.id,
      resumeToId: destination.id,
      delayMs: 10000 + Math.round(plane.seats * 34),
      penalty: Math.round(plane.seats * 30 + route.distance * 0.9)
    };
  }

  function maybeCreateFlightEvent(route, plane, origin, destination, state, position) {
    if (state.event || state.phase === "ground" || state.progress < 0.18 || state.progress > 0.78) return null;
    if (Date.now() - (state.lastEventAt || 0) < 90000) return null;
    const chance = 0.00018 * Math.max(1, speed);
    if (Math.random() > chance) return null;
    const roll = Math.random();
    const nearest = nearestAirportFromPoint(position, [origin.id, destination.id]);
    if (!nearest) return null;
    if (roll < 0.38) {
      return {
        type: "diversion",
        title: "Odklon letu",
        description: `${plane.model} mění trasu na ${airportCode(nearest)} kvůli provozní situaci.`,
        targetAirportId: nearest.id,
        resumeToId: destination.id,
        delayMs: 9000 + Math.round(plane.seats * 34),
        penalty: Math.round(plane.seats * 28 + route.distance * 0.9)
      };
    }
    if (roll < 0.62) {
      return {
        type: "emergency",
        title: "Nouzové přistání",
        description: `${plane.model} musí nouzově přistát na ${airportCode(nearest)}.`,
        targetAirportId: nearest.id,
        resumeToId: destination.id,
        delayMs: 15000 + Math.round(plane.seats * 52),
        penalty: Math.round(plane.seats * 95 + route.distance * 1.8),
        conditionLoss: 5 + Math.random() * 8
      };
    }
    return null;
  }

  function applyCompletedFlights(completed) {
    setGame(function (current) {
      let cashChange = 0;
      const statsAdd = { flights: 0, passengers: 0, revenue: 0, costs: 0, profit: 0, diversions: 0, emergencies: 0 };
      const updates = new Map();
      const planeConditionLoss = new Map();
      const logs = [];

      completed.forEach(function (item) {
        const route = current.routes.find(function (currentRoute) { return currentRoute.id === item.route.id; });
        const plane = current.fleet.find(function (currentPlane) { return currentPlane.id === item.plane.id; });
        if (!route || !plane || !item.manifest) return;
        const eco = item.manifest;
        const eventPenalty = item.eventPenalty || 0;
        const profit = eco.profit - eventPenalty;
        cashChange += profit;
        statsAdd.flights += 1;
        statsAdd.passengers += eco.passengers;
        statsAdd.revenue += eco.revenue;
        statsAdd.costs += eco.costs + eventPenalty;
        statsAdd.profit += profit;
        if (item.eventType === "diversion") statsAdd.diversions += 1;
        if (item.eventType === "emergency") statsAdd.emergencies += 1;
        const conditionLoss = Math.max(0.05, Math.min(2.4, route.distance / Math.max(500, plane.range) * (plane.seats > 120 ? 0.72 : 0.46))) + (item.conditionLoss || 0);
        planeConditionLoss.set(plane.id, (planeConditionLoss.get(plane.id) || 0) + conditionLoss);
        updates.set(route.id, {
          loadFactor: eco.loadFactor,
          eco: { ...eco, profit, costs: eco.costs + eventPenalty },
          label: eco.label,
          eventType: item.eventType || ""
        });
        logs.push(`${eco.label}: ${money(profit)} · ${eco.passengers} cestujících · ${Math.round(eco.loadFactor * 100)} %`);
      });

      if (!updates.size) return current;

      const nextRoutes = current.routes.map(function (route) {
        const update = updates.get(route.id);
        if (!update) return route;
        return {
          ...route,
          reputation: Math.min(100, Math.max(0, route.reputation + (update.eco.profit > 0 ? 0.05 : -0.12))),
          totalProfit: (route.totalProfit || 0) + update.eco.profit,
          totalPassengers: (route.totalPassengers || 0) + update.eco.passengers,
          completedFlights: (route.completedFlights || 0) + 1,
          lastProfit: update.eco.profit,
          lastLoad: update.loadFactor,
          lastRevenue: update.eco.revenue,
          lastCosts: update.eco.costs,
          lastPassengers: update.eco.passengers,
          lastFlightLabel: update.label,
          lastEvent: update.eventType
        };
      });

      const nextFleet = current.fleet.map(function (plane) {
        const loss = planeConditionLoss.get(plane.id) || 0;
        if (!loss) return plane;
        return { ...plane, condition: Math.max(8, planeCondition(plane) - loss) };
      });

      const bestUpdate = Array.from(updates.values()).reduce(function (best, item) {
        if (!best || item.eco.profit > best.eco.profit) return item;
        return best;
      }, null);

      const longest = current.routes.reduce(function (best, route) {
        return route.distance > best.distance ? route : best;
      }, { distance: current.stats.longestRoute || 0, id: null });
      const longestOrigin = longest.id ? getAirport(airports, longest.originId) : null;
      const longestDestination = longest.id ? getAirport(airports, longest.destinationId) : null;
      const longestLabel = longestOrigin && longestDestination ? `${airportCode(longestOrigin)} ⇄ ${airportCode(longestDestination)}` : current.stats.longestRouteLabel;

      return {
        ...current,
        cash: current.cash + cashChange,
        fleet: nextFleet,
        routes: nextRoutes,
        stats: {
          ...current.stats,
          flights: current.stats.flights + statsAdd.flights,
          passengers: current.stats.passengers + statsAdd.passengers,
          revenue: current.stats.revenue + statsAdd.revenue,
          costs: current.stats.costs + statsAdd.costs,
          profit: current.stats.profit + statsAdd.profit,
          diversions: (current.stats.diversions || 0) + statsAdd.diversions,
          emergencies: (current.stats.emergencies || 0) + statsAdd.emergencies,
          bestFlightProfit: bestUpdate && bestUpdate.eco.profit > current.stats.bestFlightProfit ? bestUpdate.eco.profit : current.stats.bestFlightProfit,
          bestFlightLabel: bestUpdate && bestUpdate.eco.profit > current.stats.bestFlightProfit ? bestUpdate.label : current.stats.bestFlightLabel,
          longestRoute: Math.max(current.stats.longestRoute || 0, longest.distance || 0),
          longestRouteLabel: longestLabel || current.stats.longestRouteLabel
        },
        log: [...logs.reverse(), ...current.log].slice(0, 14)
      };
    });
  }

  useEffect(function () {
    const interval = setInterval(function () {
      setRouteProgress(function (current) {
        const completed = [];
        const next = { ...current };

        game.routes.forEach(function (route) {
          const plane = getPlane(game.fleet, route.planeId);
          const origin = getAirport(airports, route.originId);
          const destination = getAirport(airports, route.destinationId);
          if (!plane || !origin || !destination) return;

          let state = typeof next[route.id] === "object" ? { ...next[route.id] } : { progress: Number(next[route.id] || 0), dir: "out" };
          if (!state.phase) state.phase = "ground";
          if (!state.dir) state.dir = "out";
          if (!state.currentFromId) state.currentFromId = state.dir === "back" ? destination.id : origin.id;
          if (!state.currentToId) state.currentToId = state.dir === "back" ? origin.id : destination.id;

          if (state.phase === "ground") {
            const groundDuration = state.groundDurationMs || groundDurationMs(plane);
            const remaining = Math.max(0, (state.groundRemainingMs ?? groundDuration) - baseTickMs * speed);
            if (remaining > 0) {
              next[route.id] = { ...state, groundDurationMs: groundDuration, groundRemainingMs: remaining };
              return;
            }
            const fromAirport = getAirport(airports, state.currentFromId) || (state.dir === "back" ? destination : origin);
            const toAirport = getAirport(airports, state.currentToId) || (state.dir === "back" ? origin : destination);
            state = {
              ...state,
              phase: "flying",
              progress: 0,
              groundRemainingMs: 0,
              groundDurationMs: groundDuration,
              tempFrom: fromAirport,
              tempTo: toAirport,
              manifest: state.manifest || makeManifest(route, plane, fromAirport, toAirport, state.revenueMultiplier || 1)
            };
          }

          const fromAirport = state.tempFrom || getAirport(airports, state.currentFromId) || (state.dir === "back" ? destination : origin);
          const toAirport = state.tempTo || getAirport(airports, state.currentToId) || (state.dir === "back" ? origin : destination);
          const legDistance = haversine(fromAirport, toAirport);
          const oneWayHours = legDistance / plane.speed + 0.45;
          const flightDurationMs = Math.max(4500, (oneWayHours / 24) * dayDurationMs);
          let progress = (state.progress || 0) + (baseTickMs * speed) / flightDurationMs;
          let event = state.event || null;
          let tempFrom = state.tempFrom || fromAirport;
          let tempTo = state.tempTo || toAirport;
          const manifest = state.manifest || makeManifest(route, plane, fromAirport, toAirport, state.revenueMultiplier || 1);
          const currentPosition = greatCirclePoint(tempFrom, tempTo, Math.max(0, Math.min(1, progress)));

          if (!event) {
            const generatedEvent = maybeCreateFlightEvent(route, plane, origin, destination, state, currentPosition);
            if (generatedEvent) {
              event = { ...generatedEvent, startedAt: Date.now() };
              pushEventAlert({ title: event.title, text: event.description, routeId: route.id, type: event.type });
              const diversionAirport = getAirport(airports, event.targetAirportId);
              if (diversionAirport) {
                tempFrom = { id: `pos-${route.id}`, name: "Aktuální pozice", type: "medium_airport", lat: currentPosition[0], lon: currentPosition[1], iata: "", icao: "" };
                tempTo = diversionAirport;
                progress = 0;
              }
            }
          }

          if (progress >= 1) {
            if (event && !state.diversionLanded) {
              const diversionAirport = getAirport(airports, event.targetAirportId);
              const resumeAirport = getAirport(airports, event.resumeToId);
              next[route.id] = {
                ...state,
                phase: "ground",
                progress: 0,
                event,
                diversionLanded: true,
                currentFromId: diversionAirport ? diversionAirport.id : state.currentFromId,
                currentToId: resumeAirport ? resumeAirport.id : state.currentToId,
                tempFrom: diversionAirport || tempTo,
                tempTo: resumeAirport || toAirport,
                groundDurationMs: event.delayMs,
                groundRemainingMs: event.delayMs,
                manifest,
                lastEventAt: Date.now()
              };
              return;
            }

            completed.push({
              route,
              plane,
              departure: fromAirport,
              arrival: toAirport,
              dir: state.dir,
              manifest,
              eventPenalty: event ? event.penalty || 0 : 0,
              conditionLoss: event ? event.conditionLoss || 0 : 0,
              eventType: event ? event.type : ""
            });

            const nextDir = state.dir === "back" ? "out" : "back";
            const nextFrom = nextDir === "back" ? destination : origin;
            const nextTo = nextDir === "back" ? origin : destination;
            next[route.id] = {
              progress: 0,
              dir: nextDir,
              phase: "ground",
              currentFromId: nextFrom.id,
              currentToId: nextTo.id,
              tempFrom: nextFrom,
              tempTo: nextTo,
              groundDurationMs: groundDurationMs(plane),
              groundRemainingMs: groundDurationMs(plane),
              manifest: null,
              lastEventAt: event ? Date.now() : state.lastEventAt
            };
            return;
          }

          next[route.id] = { ...state, phase: "flying", progress, event, tempFrom, tempTo, manifest };
        });

        Object.keys(next).forEach(function (key) {
          if (!game.routes.some(function (route) { return route.id === key; })) delete next[key];
        });

        if (completed.length) {
          setTimeout(function () {
            applyCompletedFlights(completed);
          }, 0);
        }

        return next;
      });
    }, baseTickMs);

    return function () {
      clearInterval(interval);
    };
  }, [airports, game.fleet, game.routes, speed]);

  function buyPlane(item) {
    if (game.cash < item.price) {
      toast.fire({ icon: "error", title: "Nemáš dost peněz." });
      return;
    }
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const plane = {
      id,
      model: item.model,
      role: item.role,
      range: item.range,
      seats: item.seats,
      speed: item.speed,
      fuelPerKm: item.fuelPerKm,
      maintenance: item.maintenance,
      purchasePrice: item.price,
      airportNeed: item.airportNeed,
      image: item.image,
      age: 0,
      condition: 100
    };
    setGame(function (current) {
      return {
        ...current,
        cash: current.cash - item.price,
        fleet: [...current.fleet, plane],
        log: [`Koupeno ${item.model} za ${money(item.price)}.`, ...current.log].slice(0, 14)
      };
    });
    toast.fire({ icon: "success", title: `Koupeno ${item.model}` });
  }

  async function sellPlane(planeId) {
    const plane = getPlane(game.fleet, planeId);
    if (!plane) return;
    const used = game.routes.some(function (route) {
      return route.planeId === planeId;
    });
    if (used) {
      toast.fire({ icon: "warning", title: "Nejdřív zruš linku, kde letadlo létá." });
      return;
    }
    const value = planeValue(plane);
    const result = await Swal.fire({
      title: "Prodat letadlo?",
      text: `${plane.model} prodáš za ${money(value)}. Prodejní cena je nižší než nákupní.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Prodat",
      cancelButtonText: "Nechat",
      confirmButtonColor: "#ffb84d",
      cancelButtonColor: "#20385e",
      background: "#07111f",
      color: "#edf6ff"
    });
    if (!result.isConfirmed) return;
    setGame(function (current) {
      return {
        ...current,
        cash: current.cash + value,
        fleet: current.fleet.filter(function (item) { return item.id !== planeId; }),
        log: [`Prodáno ${plane.model} za ${money(value)}.`, ...current.log].slice(0, 14)
      };
    });
    toast.fire({ icon: "success", title: `Prodáno za ${money(value)}` });
  }

  async function servicePlane(planeId) {
    const plane = getPlane(game.fleet, planeId);
    if (!plane) return;
    const used = game.routes.some(function (route) { return route.planeId === planeId; });
    if (used) {
      toast.fire({ icon: "warning", title: "Servis jde jen u volného letadla." });
      return;
    }
    const cost = serviceCost(plane);
    if (cost <= 0) {
      toast.fire({ icon: "info", title: "Letadlo je v perfektním stavu." });
      return;
    }
    if (game.cash < cost) {
      toast.fire({ icon: "error", title: "Nemáš dost peněz na servis." });
      return;
    }
    const result = await Swal.fire({
      title: "Servisovat letadlo?",
      text: `${plane.model}: stav ${Math.round(planeCondition(plane))} %. Cena servisu ${money(cost)}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Servisovat",
      cancelButtonText: "Nechat",
      confirmButtonColor: "#ffd166",
      cancelButtonColor: "#20385e",
      background: "#07111f",
      color: "#edf6ff"
    });
    if (!result.isConfirmed) return;
    setGame(function (current) {
      return {
        ...current,
        cash: current.cash - cost,
        fleet: current.fleet.map(function (item) { return item.id === planeId ? { ...item, condition: 100 } : item; }),
        stats: { ...current.stats, serviceSpent: (current.stats.serviceSpent || 0) + cost },
        log: [`Servis ${plane.model}: ${money(cost)}.`, ...current.log].slice(0, 14)
      };
    });
  }

  function runExpressFlight() {
    const now = Date.now();
    if (now < (game.expressCooldownUntil || 0)) {
      toast.fire({ icon: "warning", title: "Expresní let má cooldown." });
      return;
    }
    if (!freePlanes.length) {
      toast.fire({ icon: "warning", title: "Potřebuješ volné letadlo." });
      return;
    }
    const plane = freePlanes[0];
    const candidates = availableAirportsForGame;
    let best = null;
    for (let i = 0; i < 180; i++) {
      const a = candidates[Math.floor(Math.random() * candidates.length)];
      const b = candidates[Math.floor(Math.random() * candidates.length)];
      if (!a || !b || a.id === b.id) continue;
      const distance = haversine(a, b);
      if (distance > 80 && distance < plane.range * 0.82) {
        best = { a, b, distance };
        break;
      }
    }
    if (!best) {
      toast.fire({ icon: "error", title: "Nenašla jsem vhodný expresní let." });
      return;
    }
    const route = { distance: best.distance, ticketPricePerKm: ticketPrice(best.distance) * 1.38, reputation: 50, lastLoad: 0.95 };
    const manifest = makeManifest(route, plane, best.a, best.b, 1.22);
    const cooldown = 120000;
    setGame(function (current) {
      return {
        ...current,
        cash: current.cash + manifest.profit,
        expressCooldownUntil: now + cooldown,
        stats: {
          ...current.stats,
          flights: current.stats.flights + 1,
          passengers: current.stats.passengers + manifest.passengers,
          revenue: current.stats.revenue + manifest.revenue,
          costs: current.stats.costs + manifest.costs,
          profit: current.stats.profit + manifest.profit,
          expressFlights: (current.stats.expressFlights || 0) + 1
        },
        fleet: current.fleet.map(function (item) { return item.id === plane.id ? { ...item, condition: Math.max(8, planeCondition(item) - 0.8) } : item; }),
        log: [`Expresní let ${manifest.label}: ${money(manifest.profit)}.`, ...current.log].slice(0, 14)
      };
    });
    toast.fire({ icon: "success", title: `Expresní let: ${money(manifest.profit)}` });
  }

  function startRoutePlanning(initialAirport) {
    if (!game.setupComplete) {
      toast.fire({ icon: "warning", title: "Nejdřív založ společnost." });
      return;
    }
    setCreateMode(true);
    setDraftRoute(initialAirport ? { originId: initialAirport.id, destinationId: "" } : { originId: "", destinationId: "" });
    setSelectedPlaneId("");
    setActiveTab("route");
    setPanelOpen(true);
    toast.fire({ icon: "info", title: initialAirport ? "Teď vyber druhé letiště." : "Klikni na letiště a vyber, jestli je startovní nebo cílové." });
  }

  async function pickAirportForRoute(airport) {
    if (!createMode) return;
    setPanelOpen(true);
    setActiveTab("route");
    setSelectedAirport(airport);
    const result = await Swal.fire({
      title: airport.name,
      text: "Chceš toto letiště použít jako startovní nebo cílové?",
      icon: "question",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Startovní",
      denyButtonText: "Cílové",
      cancelButtonText: "Zrušit",
      confirmButtonColor: "#ffd166",
      denyButtonColor: "#75d5ff",
      cancelButtonColor: "#20385e",
      background: "#07111f",
      color: "#edf6ff",
      allowOutsideClick: true
    });

    if (result.isDismissed) return;

    if (result.isConfirmed) {
      setDraftRoute(function (current) {
        return { originId: airport.id, destinationId: current.destinationId === airport.id ? "" : current.destinationId };
      });
      toast.fire({ icon: "success", title: "Startovní letiště vybráno." });
      return;
    }

    setDraftRoute(function (current) {
      if (current.originId === airport.id) {
        toast.fire({ icon: "warning", title: "Cílové letiště musí být jiné než startovní." });
        return current;
      }
      return { originId: current.originId, destinationId: airport.id };
    });
    toast.fire({ icon: "success", title: "Cílové letiště vybráno." });
  }

  function clearDraftRoute() {
    setDraftRoute({ originId: "", destinationId: "" });
    setSelectedPlaneId("");
    setCreateMode(false);
    setSelectedAirport(null);
  }

  function createRoute() {
    if (!canCreateRoute) {
      toast.fire({ icon: "warning", title: "Linka nejde otevřít." });
      return;
    }
    const route = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      originId: draftOrigin.id,
      destinationId: draftDestination.id,
      planeId: selectedPlane.id,
      distance: createDistance,
      ticketPricePerKm: ticketPrice(createDistance),
      reputation: 42,
      totalProfit: 0,
      totalPassengers: 0,
      completedFlights: 0,
      lastProfit: 0,
      lastLoad: 0.9,
      lastRevenue: 0,
      lastCosts: 0,
      lastPassengers: 0,
      lastFlightLabel: ""
    };
    setGame(function (current) {
      return {
        ...current,
        routes: [...current.routes, route],
        stats: {
          ...current.stats,
          longestRoute: Math.max(current.stats.longestRoute || 0, route.distance),
          longestRouteLabel: route.distance > (current.stats.longestRoute || 0) ? `${draftOrigin.iata || draftOrigin.icao} ⇄ ${draftDestination.iata || draftDestination.icao}` : current.stats.longestRouteLabel
        },
        log: [`Otevřena linka ${draftOrigin.iata || draftOrigin.icao} ⇄ ${draftDestination.iata || draftDestination.icao}.`, ...current.log].slice(0, 14)
      };
    });
    setRouteProgress(function (current) {
      return {
        ...current,
        [route.id]: {
          progress: 0,
          dir: "out",
          phase: "ground",
          groundDurationMs: groundDurationMs(selectedPlane),
          groundRemainingMs: groundDurationMs(selectedPlane),
          currentFromId: draftOrigin.id,
          currentToId: draftDestination.id,
          tempFrom: draftOrigin,
          tempTo: draftDestination,
          manifest: null
        }
      };
    });
    setSelectedPlaneId("");
    setCreateMode(false);
    setDraftRoute({ originId: "", destinationId: "" });
    setActiveTab("routes");
    toast.fire({ icon: "success", title: "Linka otevřena" });
  }

  async function removeRoute(routeId) {
    const result = await Swal.fire({
      title: "Zrušit linku?",
      text: "Letadlo se vrátí mezi volná letadla.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Zrušit",
      cancelButtonText: "Nechat",
      confirmButtonColor: "#ff6b6b",
      cancelButtonColor: "#20385e",
      background: "#07111f",
      color: "#edf6ff"
    });
    if (!result.isConfirmed) return;
    setGame(function (current) {
      return {
        ...current,
        routes: current.routes.filter(function (route) { return route.id !== routeId; }),
        log: ["Linka zrušena.", ...current.log].slice(0, 14)
      };
    });
    setRouteProgress(function (current) {
      const next = { ...current };
      delete next[routeId];
      return next;
    });
    toast.fire({ icon: "success", title: "Linka zrušena" });
  }

  async function resetGame() {
    const result = await Swal.fire({
      title: "Resetovat hru?",
      text: "Vymaže se kapitál, flotila, linky i celý postup.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Resetovat",
      cancelButtonText: "Ne",
      confirmButtonColor: "#ff6b6b",
      cancelButtonColor: "#20385e",
      background: "#07111f",
      color: "#edf6ff",
      allowOutsideClick: true
    });
    if (!result.isConfirmed) return;
    localStorage.removeItem(saveKey);
    setGame(initialGame);
    setRouteProgress({});
    setSelectedPlaneId("");
    setActiveTab("route");
    setDraftRoute({ originId: "", destinationId: "" });
    setCreateMode(false);
    setSelectedAirport(null);
    setSetupCompanyName("");
    setSetupStartCountry("");
    toast.fire({ icon: "success", title: "Hra resetována" });
  }

  function focusAirport(airport) {
    setSelectedAirport(airport);
    setPanelOpen(true);
    if (mapRef.current) {
      mapRef.current.flyTo([airport.lat, airport.lon], Math.max(6, mapRef.current.getZoom()), { duration: 0.45 });
    }
    toast.fire({ icon: "info", title: airport.name });
  }

  function finishGameSetup() {
    const name = setupCompanyName.trim();
    if (!name || !setupStartCountry) {
      toast.fire({ icon: "warning", title: "Vyplň název společnosti a vyber stát." });
      return;
    }
    const country = countryCatalog.find(function (item) { return item.code === setupStartCountry; });
    setGame(function (current) {
      return {
        ...current,
        companyName: name,
        startCountry: setupStartCountry,
        unlockedCountries: [setupStartCountry],
        setupComplete: true,
        log: [
          `Společnost ${name} založena. Startovní stát: ${country ? country.name : setupStartCountry}.`,
          `Počáteční kapitál: ${money(current.cash)}.`
        ]
      };
    });
    setPanelOpen(true);
    setActiveTab("countries");
    setSelectedAirport(null);
    toast.fire({ icon: "success", title: `Společnost ${name} byla založena.` });
  }

  function unlockCountry(countryCode) {
    const country = countryCatalog.find(function (item) { return item.code === countryCode; });
    if (!country) return;
    if ((game.unlockedCountries || []).includes(countryCode)) return;
    if (game.cash < country.price) {
      toast.fire({ icon: "error", title: "Na odemčení této země nemáš dost peněz." });
      return;
    }
    setGame(function (current) {
      return {
        ...current,
        cash: current.cash - country.price,
        unlockedCountries: [...current.unlockedCountries, countryCode],
        log: [`Odemčena země ${country.name} za ${money(country.price)}.`, ...current.log].slice(0, 14)
      };
    });
    toast.fire({ icon: "success", title: `${country.name} odemčena` });
  }

  function triggerDevEvent() {
    const route = game.routes.find(function (item) { return item.id === devRouteId; }) || game.routes[0];
    if (!route) {
      toast.fire({ icon: "error", title: "Nejdřív vytvoř linku" });
      return;
    }
    const plane = getPlane(game.fleet, route.planeId);
    const origin = getAirport(airports, route.originId);
    const destination = getAirport(airports, route.destinationId);
    if (!plane || !origin || !destination) return;
    const currentState = typeof routeProgress[route.id] === "object" ? routeProgress[route.id] : { progress: Number(routeProgress[route.id] || 0), dir: "out" };
    const safeState = {
      ...currentState,
      phase: "flying",
      progress: Math.max(0.22, Math.min(0.72, currentState.progress || 0.35)),
      currentFromId: currentState.currentFromId || origin.id,
      currentToId: currentState.currentToId || destination.id,
      tempFrom: currentState.tempFrom || origin,
      tempTo: currentState.tempTo || destination,
      manifest: currentState.manifest || makeManifest(route, plane, origin, destination, currentState.revenueMultiplier || 1)
    };
    const point = planePoint(origin, destination, safeState);
    const event = manualEventFor(devEventType, route, plane, origin, destination, point.position);
    setRouteProgress(function (current) {
      const next = { ...current };
      const target = getAirport(airports, event.targetAirportId);
      next[route.id] = {
        ...safeState,
        event,
        progress: 0,
        tempFrom: { id: `dev-pos-${route.id}`, name: "Aktuální pozice", type: "medium_airport", lat: point.position[0], lon: point.position[1], iata: "", icao: "" },
        tempTo: target || destination,
        currentFromId: safeState.currentFromId,
        currentToId: event.targetAirportId,
        lastEventAt: Date.now()
      };
      return next;
    });
    pushEventAlert({ title: event.title, text: event.description, routeId: route.id, type: event.type });
  }

  function renderRouteTab() {
    return (
      <RoutePlannerTab
        draftOrigin={draftOrigin}
        draftDestination={draftDestination}
        createDistance={createDistance}
        number={number}
        createMode={createMode}
        onStartRoutePlanning={startRoutePlanning}
        onClearDraftRoute={clearDraftRoute}
        selectedAirport={selectedAirport}
        onUseAirportAsStart={startRoutePlanning}
        selectedPlaneId={selectedPlaneId}
        onSelectPlane={setSelectedPlaneId}
        freePlanes={freePlanes}
        selectedPlane={selectedPlane}
        routePreview={routePreview}
        routePreviewRange={routePreviewRange}
        routePreviewDifficulty={routePreviewDifficulty}
        routeAdvice={routeAdvice}
        canCreateRoute={canCreateRoute}
        onCreateRoute={createRoute}
        money={money}
      />
    );
  }

  function renderFleetTab() {
    return (
      <FleetTab
        game={game}
        routes={game.routes}
        airports={airports}
        number={number}
        money={money}
        airportTypeLabel={airportTypeLabel}
        planeCondition={planeCondition}
        planeValue={planeValue}
        serviceCost={serviceCost}
        getAirport={getAirport}
        onBuyPlane={buyPlane}
        onServicePlane={servicePlane}
        onSellPlane={sellPlane}
      />
    );
  }

  function renderCountriesTab() {
    return (
      <CountriesTab
        countries={countryCatalog}
        unlockedCountries={game.unlockedCountries || []}
        gameCash={game.cash}
        onUnlockCountry={unlockCountry}
        money={money}
        number={number}
      />
    );
  }

  function renderAirportsTab() {
    return (
      <AirportsTab
        loadingAirports={loadingAirports}
        airports={airports}
        visibleAirports={visibleAirports}
        search={search}
        onSearchChange={setSearch}
        onAirportFocus={focusAirport}
        airportTypeLabels={airportTypeLabels}
        setupComplete={game.setupComplete}
      />
    );
  }

  function renderRoutesTab() {
    return (
      <div className="tabContent">
        <div className="sectionTitle">
          <div>
            <h2>Aktivní linky</h2>
            <p>{game.routes.length ? "Peníze se přičítají po každém doletu." : "Zatím nemáš žádnou linku."}</p>
          </div>
        </div>
        <div className="routeList">
          {game.routes.map(function (route) {
            const routeOrigin = getAirport(airports, route.originId);
            const routeDestination = getAirport(airports, route.destinationId);
            const plane = getPlane(game.fleet, route.planeId);
            if (!routeOrigin || !routeDestination || !plane) return null;
            const eco = routeEconomy(route, plane, routeOrigin, routeDestination);
            const ecoRange = estimateRouteRange(route, plane, routeOrigin, routeDestination);
            return (
              <article key={route.id} className="routeCard">
                <div>
                  <strong>{routeOrigin.iata || routeOrigin.icao} ⇄ {routeDestination.iata || routeDestination.icao}</strong>
                  <span>{plane.model} · {number(route.distance)} km</span>
                </div>
                <div className="miniStats">
                  <span>{money(route.lastProfit || 0)}<em>poslední dolet</em></span>
                  <span>{money(ecoRange.low.oneFlight.profit)} až {money(ecoRange.high.oneFlight.profit)}<em>let 80–100 %</em></span>
                  <span>{route.lastPassengers || 0}<em>poslední cestující</em></span>
                  <span>{Math.round((route.lastLoad || 0.9) * 100)}%<em>poslední obsazenost</em></span>
                  <span>{number(route.completedFlights || 0)}<em>dokončené lety</em></span>
                  <span>{money(route.totalProfit || 0)}<em>celkem</em></span>
                </div>
                <details className="routeDetails">
                  <summary>Detaily náročnosti</summary>
                  <p>Využití doletu: {Math.round((route.distance / plane.range) * 100)}%</p>
                  <p>Odhad letů za den: {eco.flightsPerDay.toFixed(1)}× při délce dne 5 minut</p>
                  <p>Průměrná letenka: {money(eco.oneFlight.ticket)}</p>
                  <p>Jeden let: palivo {money(eco.oneFlight.fuel)}, letištní poplatky {money(eco.oneFlight.airportFees)}, posádka {money(eco.oneFlight.staff)}, údržba {money(eco.oneFlight.maintenance)}</p>
                  <p>Poslední let: {route.lastFlightLabel || "zatím žádný"}, tržby {money(route.lastRevenue || 0)}, náklady {money(route.lastCosts || 0)}</p>
                  <p>Doporučení: {routeAdvice(route.distance, plane, eco)}</p>
                </details>
                <button onClick={function () { removeRoute(route.id); }}>Zrušit</button>
              </article>
            );
          })}
        </div>
      </div>
    );
  }

  function renderActionsTab() {
    const ready = expressRemaining <= 0;
    return (
      <div className="tabContent">
        <div className="sectionTitle">
          <div>
            <h2>Zakázky a eventy</h2>
            <p>Krátké akce pro chvíle, kdy nechceš jen čekat na mapě.</p>
          </div>
        </div>
        <div className="actionGrid">
          <article className="actionCard">
            <strong>Expresní jednorázový let</strong>
            <p>Vezme první volné letadlo, najde vhodnou krátkou zakázku a rovnou ji vyhodnotí. Má cooldown, aby z toho nebyla tiskárna peněz.</p>
            <div className="miniStats">
              <span>{freePlanes.length}<em>volná letadla</em></span>
              <span>{ready ? "připraveno" : `${Math.ceil(expressRemaining / 1000)} s`}<em>cooldown</em></span>
            </div>
            <button className="primary" disabled={!ready || !freePlanes.length} onClick={runExpressFlight}>Vzít expresní let</button>
          </article>
          <article className="actionCard">
            <strong>Letové události</strong>
            <p>Na některých letech se může objevit odklon nebo nouzové přistání. Když k tomu dojde, uvidíš upozornění vpravo nahoře.</p>
            <div className="miniStats">
              <span>{number(game.stats.emergencies || 0)}<em>nouzová přistání</em></span>
              <span>{number(game.stats.diversions || 0)}<em>odklony letů</em></span>
            </div>
          </article>
          <article className="actionCard">
            <strong>Servis flotily</strong>
            <p>Volná letadla můžeš servisovat v záložce Letadla. Horší stav zvyšuje náklady a snižuje prodejní cenu.</p>
            <div className="miniStats">
              <span>{money(game.stats.serviceSpent || 0)}<em>utraceno za servis</em></span>
              <span>{number(game.stats.diversions || 0)}<em>odklony</em></span>
            </div>
          </article>
        </div>
      </div>
    );
  }

  function renderStatsTab() {
    const margin = game.stats.revenue > 0 ? Math.round((game.stats.profit / game.stats.revenue) * 100) : 0;
    return (
      <div className="tabContent">
        <div className="sectionTitle">
          <div>
            <h2>Globální statistiky</h2>
            <p>Souhrn celé společnosti.</p>
          </div>
        </div>
        <div className="globalStats">
          <span><b>{number(game.stats.flights)}</b><em>dokončené lety</em></span>
          <span><b>{number(game.stats.passengers)}</b><em>přepravení cestující</em></span>
          <span><b>{money(game.stats.revenue)}</b><em>celkové tržby</em></span>
          <span><b>{money(game.stats.costs)}</b><em>celkové náklady</em></span>
          <span><b>{money(game.stats.profit)}</b><em>celkový zisk</em></span>
          <span><b>{margin}%</b><em>marže</em></span>
          <span><b>{money(game.stats.bestFlightProfit)}</b><em>nejlepší let</em></span>
          <span><b>{game.stats.bestFlightLabel || "-"}</b><em>nejlepší trasa</em></span>
          <span><b>{number(game.stats.longestRoute)} km</b><em>nejdelší linka</em></span>
          <span><b>{game.stats.longestRouteLabel || "-"}</b><em>nejdelší trasa</em></span>
          <span><b>{number(game.stats.diversions || 0)}</b><em>odklony</em></span>
          <span><b>{number(game.stats.emergencies || 0)}</b><em>nouzová přistání</em></span>
          <span><b>{money(game.stats.serviceSpent || 0)}</b><em>servis</em></span>
          <span><b>{number(game.stats.expressFlights || 0)}</b><em>expresní lety</em></span>
        </div>
      </div>
    );
  }

  function renderGameTab() {
    return (
      <div className="tabContent">
        <div className="sectionTitle">
          <div>
            <h2>Hra</h2>
            <p>Tempo a poslední události.</p>
          </div>
        </div>
        <div className="speedBox">
          <span>Rychlost hry</span>
          <strong>{speed}× · 1 den = {Math.round(300 / speed)} s</strong>
          <input type="range" min="1" max="8" value={speed} onChange={function (event) { setSpeed(Number(event.target.value)); }} />
          <small>Základní tempo je 1 herní den za 5 minut.</small>
        </div>
        <div className="logList">
          {game.log.map(function (entry, index) {
            return <p key={`${entry}-${index}`}>{entry}</p>;
          })}
        </div>
        <button className="danger" onClick={resetGame}>Resetovat hru</button>
      </div>
    );
  }

  function renderTab() {
    if (activeTab === "fleet") return renderFleetTab();
    if (activeTab === "airports") return renderAirportsTab();
    if (activeTab === "countries") return renderCountriesTab();
    if (activeTab === "routes") return renderRoutesTab();
    if (activeTab === "actions") return renderActionsTab();
    if (activeTab === "stats") return renderStatsTab();
    if (activeTab === "game") return renderGameTab();
    return renderRouteTab();
  }

  return (
    <div className="app">
      <main className="mapArea">
        <WorldMap
          mapCenter={mapCenter}
          mapRef={mapRef}
          createMode={createMode}
          airports={airports}
          getAirport={getAirport}
          draftOrigin={draftOrigin}
          draftDestination={draftDestination}
          greatCirclePositions={greatCirclePositions}
          routes={game.routes}
          fleet={game.fleet}
          getPlane={getPlane}
          routeProgress={routeProgress}
          planePoint={planePoint}
          planeIcon={planeIcon}
          airportCode={airportCode}
          money={money}
          planeCondition={planeCondition}
          visibleAirports={visibleAirports}
          draftRoute={draftRoute}
          onAirportPick={pickAirportForRoute}
          airportTypeLabels={airportTypeLabels}
          airportMarkerRadius={airportMarkerRadius}
          onMapBackgroundClick={function () {
            if (!hasOpenMapPopup) setSelectedAirport(null);
          }}
          onPopupChange={setHasOpenMapPopup}
        />
      </main>

      <header className="hud">
        <div className="brandMini"><span>✈</span><strong>{game.companyName || "Airline Tycoon"}</strong></div>
        <div><span>Kapitál</span><strong>{money(game.cash)}</strong></div>
        <div><span>Den</span><strong>{game.day}</strong></div>
        <div><span>Startovní stát</span><strong>{game.setupComplete ? startCountryName : "-"}</strong></div>
        <div><span>Letadla</span><strong>{game.fleet.length}</strong></div>
        <div><span>Linky</span><strong>{game.routes.length}</strong></div>
        <div><span>Lety</span><strong>{number(game.stats.flights)}</strong></div>
      </header>

      {showDevTools && <button className="devButton" onClick={function () { setDevOpen(true); }}>DEV</button>}

      {devOpen && (
        <div className="devOverlay" onMouseDown={function (event) { if (event.target.className === "devOverlay") setDevOpen(false); }}>
          <div className="devModal">
            <div className="devHeader">
              <div>
                <strong>Dev panel</strong>
                <span>Testování ekonomiky a letových událostí</span>
              </div>
              <button onClick={function () { setDevOpen(false); }}>×</button>
            </div>

            <div className="devBlock">
              <label>
                Kapitál
                <input value={devBalance} onChange={function (event) { setDevBalance(event.target.value); }} inputMode="numeric" />
              </label>
              <button className="primary" onClick={applyDevBalance}>Nastavit kapitál</button>
            </div>

            <div className="devBlock">
              <label>
                Let
                <select value={devRouteId} onChange={function (event) { setDevRouteId(event.target.value); }}>
                  {game.routes.length === 0 && <option value="">Žádný aktivní let</option>}
                  {game.routes.map(function (route) {
                    const origin = getAirport(airports, route.originId);
                    const destination = getAirport(airports, route.destinationId);
                    const plane = getPlane(game.fleet, route.planeId);
                    return <option key={route.id} value={route.id}>{plane ? plane.model : "Letadlo"} · {origin ? airportCode(origin) : "?"} ⇄ {destination ? airportCode(destination) : "?"}</option>;
                  })}
                </select>
              </label>
              <label>
                Event
                <select value={devEventType} onChange={function (event) { setDevEventType(event.target.value); }}>
                  <option value="diversion">Odklon letu</option>
                  <option value="emergency">Nouzové přistání</option>
                </select>
              </label>
              <button className="primary" disabled={!game.routes.length} onClick={triggerDevEvent}>Spustit event</button>
            </div>
          </div>
        </div>
      )}

      <div className="eventAlerts">
        {eventAlerts.map(function (alert) {
          return (
            <button key={alert.id} className={`eventAlert ${alert.type || ""}`} onClick={function () { focusFlight(alert.routeId); }}>
              <strong>{alert.title}</strong>
              <span>{alert.text}</span>
              <em>Klikni pro zaměření letu</em>
            </button>
          );
        })}
      </div>

      {!game.setupComplete && (
        <NewGameSetupModal
          companyName={setupCompanyName}
          startCountry={setupStartCountry}
          countryOptions={countryCatalog}
          onCompanyNameChange={setSetupCompanyName}
          onStartCountryChange={setSetupStartCountry}
          onSubmit={finishGameSetup}
          canSubmit={Boolean(setupCompanyName.trim() && setupStartCountry)}
        />
      )}

      <section className={panelOpen ? "controlPanel open" : "controlPanel"}>
        <button className="panelToggle" onClick={function () { setPanelOpen(!panelOpen); }}>{panelOpen ? "Skrýt ovládání" : "Otevřít ovládání"}</button>
        <nav className="tabs">
          <button className={activeTab === "route" ? "active" : ""} onClick={function () { setActiveTab("route"); setPanelOpen(true); }}>Linka</button>
          <button className={activeTab === "fleet" ? "active" : ""} onClick={function () { setActiveTab("fleet"); setPanelOpen(true); }}>Letadla</button>
          <button className={activeTab === "countries" ? "active" : ""} onClick={function () { setActiveTab("countries"); setPanelOpen(true); }}>Země</button>
          <button className={activeTab === "airports" ? "active" : ""} onClick={function () { setActiveTab("airports"); setPanelOpen(true); }}>Letiště</button>
          <button className={activeTab === "routes" ? "active" : ""} onClick={function () { setActiveTab("routes"); setPanelOpen(true); }}>Spoje</button>
          <button className={activeTab === "actions" ? "active" : ""} onClick={function () { setActiveTab("actions"); setPanelOpen(true); }}>Akce</button>
          <button className={activeTab === "stats" ? "active" : ""} onClick={function () { setActiveTab("stats"); setPanelOpen(true); }}>Statistiky</button>
          <button className={activeTab === "game" ? "active" : ""} onClick={function () { setActiveTab("game"); setPanelOpen(true); }}>Hra</button>
        </nav>
        {panelOpen && renderTab()}
      </section>
    </div>
  );
}
